import dayjs, { Dayjs } from 'dayjs';
import {
	UserAvailabilityPayload,
	createUserAvailability,
	deleteUserAvailability,
	updateUserAvailability,
} from 'core/API/userAvailabilities';
import { UserAvailability } from 'core/interfaces/userInterface';
import {
	WeekDay,
	extractDayNumericId,
	getLatestAvailabilityForDay,
	normalizeDayIri,
	toDayjsAvailabilityTime,
} from 'module/schedule/utils/availabilityDisplay';
import { isProductLicensed } from 'module/dashboard/utils/homePage';

export type OnboardingDayAvailability = {
	day: WeekDay;
	enabled: boolean;
	startTime: Dayjs | null;
	endTime: Dayjs | null;
};

export type OnboardingStepKey =
	| 'basicInfo'
	| 'availability'
	| 'documents'
	| 'submit'
	| 'complete';

export type OnboardingStepIndices = {
	basicInfo: number;
	availability: number | null;
	documents: number | null;
	submit: number;
	complete: number | null;
};

/** True when the organization has an active Scheduler (Scheduling Module) license. */
export function isSchedulingModuleLicensed(
	products: Array<{ label: string }> | null | undefined,
): boolean {
	return isProductLicensed(products || [], 'Scheduler');
}

export function buildOnboardingStepLabels(options: {
	hasDocuments: boolean;
	schedulingLicensed: boolean;
}): string[] {
	const { hasDocuments, schedulingLicensed } = options;
	const steps = ['Basic Information'];

	if (schedulingLicensed) {
		steps.push('Availability');
	}

	if (hasDocuments) {
		steps.push('Fill Documents', 'Review & Submit');
	} else {
		steps.push('Submit', 'Complete');
	}

	return steps;
}

export function resolveOnboardingStepIndices(options: {
	hasDocuments: boolean;
	schedulingLicensed: boolean;
}): OnboardingStepIndices {
	const { hasDocuments, schedulingLicensed } = options;
	let next = 0;
	const basicInfo = next++;
	const availability = schedulingLicensed ? next++ : null;
	const documents = hasDocuments ? next++ : null;
	const submit = next++;
	const complete = hasDocuments ? null : next;

	return { basicInfo, availability, documents, submit, complete };
}

export function resolveInitialOnboardingStep(options: {
	basicInfoCompleted: boolean;
	availabilityCompleted: boolean;
	allDocumentsCompleted: boolean;
	isSubmitted: boolean;
	hasDocuments: boolean;
	schedulingLicensed: boolean;
}): number {
	const indices = resolveOnboardingStepIndices(options);
	const {
		basicInfoCompleted,
		availabilityCompleted,
		allDocumentsCompleted,
		isSubmitted,
		schedulingLicensed,
	} = options;

	if (!basicInfoCompleted) {
		return indices.basicInfo;
	}

	if (schedulingLicensed && !availabilityCompleted && indices.availability != null) {
		return indices.availability;
	}

	if (options.hasDocuments) {
		if (!allDocumentsCompleted && indices.documents != null) {
			return indices.documents;
		}
		return indices.submit;
	}

	if (isSubmitted && indices.complete != null) {
		return indices.complete;
	}

	return indices.submit;
}

export function toWorkplaceIri(workplaceId: number | string): string {
	const id = String(workplaceId).replace(/^\/?api\/workplaces\//, '');
	return `/api/workplaces/${id}`;
}

export function toDayIri(day: WeekDay | string | number): string {
	if (typeof day === 'string') {
		const normalized = normalizeDayIri(day);
		return normalized.startsWith('api/') ? `/${normalized}` : normalized.startsWith('/') ? normalized : `/api/days/${normalized}`;
	}
	if (typeof day === 'number') {
		return `/api/days/${day}`;
	}
	if (day['@id']) {
		const normalized = normalizeDayIri(day['@id']);
		return normalized.startsWith('/') ? normalized : `/${normalized}`;
	}
	const numericId = extractDayNumericId(day);
	if (numericId == null) {
		throw new Error('Unable to resolve day IRI for availability');
	}
	return `/api/days/${numericId}`;
}

/**
 * Formats TimePicker values as HH:mm:ss, matching AvailabilityForm / shift request payloads.
 */
export function toAvailabilityTimeString(time: Dayjs): string {
	return dayjs(time).format('HH:mm:ss');
}

export function buildUserAvailabilityPayload(options: {
	workplaceId: number | string;
	day: WeekDay;
	startTime: Dayjs;
	endTime: Dayjs;
	userId?: number | string;
}): UserAvailabilityPayload {
	const { workplaceId, day, startTime, endTime, userId } = options;
	const startClock = toAvailabilityTimeString(startTime);
	const endClock = toAvailabilityTimeString(endTime);

	// Equal clock times are a 24h window. Persist as next-day end so the API does not
	// collapse them into same-day equal timestamps after round-trip.
	const startAt =
		startClock === endClock ? `2000-01-01T${startClock}` : startClock;
	const endAt = startClock === endClock ? `2000-01-02T${endClock}` : endClock;

	const payload: UserAvailabilityPayload = {
		workplace: toWorkplaceIri(workplaceId),
		day: toDayIri(day),
		startAt,
		endAt,
	};
	if (userId != null && String(userId) !== '') {
		payload.user = `/api/users/${userId}`;
	}
	return payload;
}

/** Normalize a single embedded availability row from heterogeneous API shapes. */
export function normalizeUserAvailabilityRecord(entry: unknown): UserAvailability | null {
	if (!entry || typeof entry !== 'object') return null;

	const row = entry as Record<string, unknown>;
	const startAt =
		row.startAt ?? row.start_at ?? row.startTime ?? row.start_time ?? null;
	const endAt = row.endAt ?? row.end_at ?? row.endTime ?? row.end_time ?? null;
	const day = row.day ?? row.Day ?? null;
	const id = row.id;

	if (startAt == null && endAt == null && day == null) return null;

	return {
		id: typeof id === 'number' ? id : Number(id) || 0,
		workplace: typeof row.workplace === 'string' ? row.workplace : String(row.workplace ?? ''),
		startAt: startAt == null ? '' : String(startAt),
		endAt: endAt == null ? '' : String(endAt),
		day: day as UserAvailability['day'],
	};
}

/**
 * Read availability from an embedded User payload (`userAvailability`).
 * Prefer this for display/prefill — do not GET `/user_availabilities` for that.
 * Create / update / delete still use the `user_availabilities` collection APIs.
 */
export function extractUserAvailabilityFromUser(
	user:
		| {
				userAvailability?: unknown;
				user_availabilities?: unknown;
				data?: {
					userAvailability?: unknown;
					user_availabilities?: unknown;
				};
		  }
		| null
		| undefined,
): UserAvailability[] {
	if (!user) return [];

	const raw =
		user.userAvailability ??
		user.user_availabilities ??
		user.data?.userAvailability ??
		user.data?.user_availabilities ??
		null;
	if (!raw) return [];

	let rows: unknown[] = [];
	if (Array.isArray(raw)) {
		rows = raw;
	} else if (typeof raw === 'object') {
		const member =
			(raw as { member?: unknown; 'hydra:member'?: unknown }).member ||
			(raw as { 'hydra:member'?: unknown })['hydra:member'];
		if (Array.isArray(member)) rows = member;
	}

	return rows
		.map((entry) => normalizeUserAvailabilityRecord(entry))
		.filter((entry): entry is UserAvailability => entry != null);
}

/** Build Formik day rows from embedded user availability (backend defaults included). */
export function buildOnboardingAvailabilityInitialValues(
	allDays: WeekDay[],
	currentAvailability: UserAvailability[],
): { availability: OnboardingDayAvailability[] } {
	return {
		availability: allDays.map((day) => {
			const latestAvail = getLatestAvailabilityForDay(currentAvailability, day);

			if (latestAvail) {
				// A stored row means the day is available (incl. 24h equal-clock windows).
				// Unchecked days are omitted from userAvailability, not stored as markers.
				const startTime = toDayjsAvailabilityTime(latestAvail.startAt);
				const endTime = toDayjsAvailabilityTime(latestAvail.endAt);

				return {
					day,
					enabled: Boolean(startTime && endTime),
					startTime,
					endTime,
				};
			}

			return {
				day,
				enabled: false,
				startTime: null,
				endTime: null,
			};
		}),
	};
}

export function hasSavedAvailability(
	currentAvailability: UserAvailability[] | null | undefined,
): boolean {
	return (currentAvailability || []).some((entry) => {
		if (!entry?.startAt || !entry?.endAt) return false;
		const start = toDayjsAvailabilityTime(entry.startAt);
		const end = toDayjsAvailabilityTime(entry.endAt);
		return Boolean(start && end);
	});
}

/**
 * Persists each day's availability to user_availabilities:
 * - enabled + times → create or update
 * - previously set day now disabled → delete existing record
 */
export async function saveOnboardingAvailability(options: {
	workplaceId: number | string;
	days: OnboardingDayAvailability[];
	currentAvailability: UserAvailability[];
	/** When set (manager editing another user), include user IRI on creates. */
	userId?: number | string;
}): Promise<void> {
	const { workplaceId, days, currentAvailability, userId } = options;
	const operations: Promise<unknown>[] = [];

	for (const dayAvailability of days) {
		const existing = getLatestAvailabilityForDay(currentAvailability, dayAvailability.day);

		if (dayAvailability.enabled) {
			if (!dayAvailability.startTime || !dayAvailability.endTime) {
				continue;
			}
			const payload = buildUserAvailabilityPayload({
				workplaceId,
				day: dayAvailability.day,
				startTime: dayAvailability.startTime,
				endTime: dayAvailability.endTime,
				userId,
			});

			if (existing?.id != null) {
				operations.push(updateUserAvailability(existing.id, payload));
			} else {
				operations.push(createUserAvailability(payload));
			}
			continue;
		}

		if (existing?.id != null) {
			operations.push(deleteUserAvailability(existing.id));
		}
	}

	await Promise.all(operations);
}
